import {
    Currency,
    formatOrders,
    formatTopPositionsStat,
    Order,
    OrderFilterType,
    TopPositionsType,
} from '@orbis-systems/orbis-api-middleware';
import { useQueries, useQuery } from '@tanstack/react-query';
import { userQueries } from '../user';
import {
    getPortfolioSecurityTotalValue,
    combineAccountsWithRtbs,
    combineBranchInfo,
    combineGroupsWithRtbs,
    combineModelsWithRtbs,
    formatPortfolio,
} from './adapters';
import { flatMap, isArray } from 'lodash';
import { branchQueries, BranchPortfolioQueryParams } from './queries';
import { UserType } from '@orbis-systems/orbis-api-middleware';
import { getQueryStates } from '../utils';
import { modelQueries } from '../models';
import { groupQueries } from '../group';
import { ordersQueries } from '../orders';
import { useCurrentBaseCurrency } from '@/components/~shared/~hooks';
import { combinePositions } from './combinePositions';
import { Portfolio } from '@/types';

export type BranchInformation = {
    balance: number;
    cash: number;
    portfolioValue: number;
    securityTotalValue: number;
    baseCurrency: Currency;
    monthlyCashflow: number;
    totalModels: number;
    totalAccounts: number;
    totalGroups: number;
};

export const useGetBranchInformationQuery = () => {
    const baseCurrency = useCurrentBaseCurrency();
    return useQueries({
        queries: [
            userQueries.userInformation(),
            branchQueries.branchRtbs(),
            modelQueries.memberRealtimeBalances({ modelId: 'models' }),
            modelQueries.listing({ loadQuotes: true, loadAccounts: true }),
            groupQueries.groupsList(),
            branchQueries.branchPortfolio({ currency: baseCurrency, combined: true }),
        ],
        combine: (results) => {
            const [
                { data: userInfo },
                { data: branchRtbs },
                { data: memberRealtimeBalances },
                { data: models },
                { data: groups },
                { data: branchPortfolio },
            ] = results;
            const queryStates = getQueryStates(results);

            if (queryStates.isSuccess) {
                const accountsWithRtbs = combineAccountsWithRtbs(
                    userInfo?.data.accountList ?? [],
                    branchRtbs?.data ?? [],
                );
                const modelsWithRtbs = combineModelsWithRtbs(
                    memberRealtimeBalances?.data ?? [],
                    models?.data ?? [],
                    accountsWithRtbs,
                );
                const groupsWithRtbs = combineGroupsWithRtbs(groups?.data ?? [], accountsWithRtbs);
                const securityTotalValue = getPortfolioSecurityTotalValue(branchPortfolio?.data?.entries ?? []);
                const result = combineBranchInfo(accountsWithRtbs, modelsWithRtbs, groupsWithRtbs);

                return {
                    data: {
                        ...result,
                        securityTotalValue,
                        baseCurrency,
                    } as BranchInformation,
                    ...queryStates,
                };
            }

            return {
                data: null,
                ...queryStates,
            };
        },
    });
};

export const useGetBranchPortfolioQuery = (params?: BranchPortfolioQueryParams) => {
    const { combined = true, symbol, positionType, enabled = true } = params || {};
    const baseCurrency = useCurrentBaseCurrency();

    return useQueries({
        queries: [
            { ...userQueries.userInformation(), enabled },
            { ...branchQueries.branchRtbs(), enabled },
            { ...branchQueries.branchInventory(), enabled },
            { ...branchQueries.branchPortfolio({ currency: baseCurrency, combined, positionType }), enabled },
        ],
        combine: (results) => {
            const [{ data: userInfo }, { data: branchInfo }, { data: branchInventory }, { data: branchPortfolio }] =
                results;

            const queryStates = getQueryStates(results);

            if (queryStates.isSuccess) {
                const accountsWithRtbs = combineAccountsWithRtbs(
                    userInfo?.data.accountList ?? [],
                    branchInfo?.data ?? [],
                );

                const inventoryAccounts = branchInventory?.data ?? [];
                const securityTotalValue = getPortfolioSecurityTotalValue(branchPortfolio?.data?.entries ?? []);

                const portfolio = formatPortfolio(
                    branchPortfolio?.data ?? undefined,
                    accountsWithRtbs,
                    inventoryAccounts,
                    securityTotalValue,
                    {
                        symbol,
                        positionType: positionType,
                    },
                );

                return {
                    data: portfolio,
                    ...queryStates,
                };
            }

            return {
                data: null,
                ...queryStates,
            };
        },
    });
};

interface UseGetBranchAccountsQueryParams {
    enabled?: boolean;
}

export const useGetBranchAccountsQuery = (params?: UseGetBranchAccountsQueryParams) => {
    const { enabled = true } = params || {};

    return useQueries({
        queries: [
            { ...userQueries.userInformation(), enabled },
            { ...branchQueries.branchRtbs(), enabled },
        ],
        combine: (results) => {
            const [{ data: userInfo }, { data: branchInfo }] = results;

            const accountsWithRtbs = combineAccountsWithRtbs(userInfo?.data.accountList ?? [], branchInfo?.data ?? []);

            return {
                accounts: accountsWithRtbs,
                ...getQueryStates(results),
            };
        },
    });
};

interface UseGetBranchAccountQueryParams {
    accountId: number;
    enabled?: boolean;
}

export const useGetBranchAccountQuery = (params: UseGetBranchAccountQueryParams) => {
    const { accountId, enabled = true } = params;

    const accountsQuery = useGetBranchAccountsQuery({ enabled });

    const account = accountsQuery.accounts.find((account) => account.accountId === accountId);

    if (!account) {
        return {
            data: null,
            ...accountsQuery,
        };
    }

    return {
        data: account,
        ...accountsQuery,
    };
};

export const useGetBranchTopPositionsBreakdownQuery = (by = TopPositionsType.Weight, maxPositions = 10) => {
    const { data: portfolio, isLoading, isSuccess } = useGetBranchPortfolioQuery();

    if (!isSuccess) {
        return {
            topPositions: [],
            isLoading,
            isSuccess,
        };
    }

    const topPositions = formatTopPositionsStat(portfolio?.entries ?? [], by, maxPositions);

    return {
        topPositions,
        isLoading,
        isSuccess,
    };
};

interface UseGetBranchUsersQueryParams {
    userTypeFilter?: UserType | UserType[];
    enabled?: boolean;
}

/**
 * Fetches branch users (clients) and optionally filters them by user type(s).
 * Data source: orbis.advisory.userAccountManagement.list()
 */
export const useGetBranchUsersQuery = (params?: UseGetBranchUsersQueryParams) => {
    const { userTypeFilter, enabled = true } = params || {};
    const query = useQuery({ ...branchQueries.usersList(), enabled });

    const allUsers = query.data?.data ?? [];
    const data = userTypeFilter
        ? allUsers.filter((user) =>
              isArray(userTypeFilter) ? userTypeFilter.includes(user.userType) : user.userType === userTypeFilter,
          )
        : allUsers;

    return { ...query, data };
};

export interface BranchOrdersQueryParams {
    orderFilter?: OrderFilterType | OrderFilterType[];
    symbol?: string;
}

export const useGetBranchOrdersQuery = (params?: BranchOrdersQueryParams) => {
    const { orderFilter = [OrderFilterType.Latest, OrderFilterType.Open, OrderFilterType.Today], symbol } =
        params || {};

    const ordersListingForBranchQueries = (isArray(orderFilter) ? orderFilter : [orderFilter]).map((filter) => {
        return ordersQueries.listingForBranch({
            type: `${filter}`,
            query: {
                loadQuotes: true,
                symbol,
            },
        });
    });

    const infoQueries = useQueries({
        queries: [modelQueries.listing(), userQueries.userInformation(), branchQueries.branchInventory()],
        combine: (results) => {
            const [models, accounts, inventoryAccounts] = results;
            return {
                models: models.data?.data,
                accounts: accounts.data?.data.accountList,
                inventoryAccounts: inventoryAccounts.data?.data,
                ...getQueryStates(results),
            };
        },
    });

    const orders = useQueries({
        queries: [...ordersListingForBranchQueries],
        combine: (results) => {
            return {
                data: flatMap(results, (item) => item.data?.data) as Order[],
                ...getQueryStates(results),
            };
        },
    });

    if (infoQueries.isSuccess && orders.isSuccess) {
        return {
            data: formatOrders({
                orders: orders.data,
                models: infoQueries.models ?? [],
                accounts: infoQueries.accounts ?? [],
                inventoryAccount: infoQueries.inventoryAccounts?.[0],
            }),
            isLoading: infoQueries.isLoading || orders.isLoading,
            isError: infoQueries.isError || orders.isError,
        };
    }

    return {
        data: [],
        isLoading: infoQueries.isLoading || orders.isLoading,
        isError: infoQueries.isError || orders.isError,
    };
};

export interface ModelPortfolioByBranchParams extends Omit<BranchPortfolioQueryParams, 'combined'> {
    modelId: string;
}

/** Default values for empty portfolio initialization */
const DEFAULT_PORTFOLIO_VALUES = {
    totalGainPct: 0,
    containsShort: false,
    homogeneous: true,
    total: 0,
    totalGain: 0,
    containsEquities: false,
    currency: '',
    containsOptions: false,
    totalCost: 0,
    totalDaysValue: 0,
    realizedProfitLoss: { USD: 0 },
    securityTotalValue: 0,
} as const;

/**
 * Custom hook to fetch and aggregate model portfolio data from branch portfolio
 *
 * This hook combines data from multiple sources:
 * - Branch real-time balances
 * - Model member accounts
 * - Branch portfolio positions
 *
 * It filters and merges positions belonging to the specified model,
 * calculating aggregated metrics and formatting the result.
 *
 * @param params - Query parameters including modelId, symbol, positionType, and enabled flag
 * @returns Combined query result with merged model portfolio data
 *
 * @example
 * const { data: modelPortfolio, isLoading } = useGetModelPortfolioFromBranchPortfolioQuery({
 *   modelId: '123',
 *   enabled: true
 * });
 */
export const useGetModelPortfolioFromBranchPortfolioQuery = (params: ModelPortfolioByBranchParams) => {
    const { modelId, symbol, positionType, enabled = true } = params || {};
    const baseCurrency = useCurrentBaseCurrency();

    return useQueries({
        queries: [
            { ...branchQueries.branchRtbs(), enabled },
            { ...modelQueries.memberAccounts({ modelId }), enabled },
            { ...branchQueries.branchPortfolio({ currency: baseCurrency, combined: false, positionType }), enabled },
        ],
        combine: (results) => {
            const [{ data: branchRtbs }, { data: memberListAccounts }, { data: branchPortfolio }] = results;
            const queryStates = getQueryStates(results);

            if (queryStates.isSuccess) {
                // Extract account data from member list
                const modelAccountIds = memberListAccounts?.data?.map((item) => item.account.accountId) ?? [];
                const modelAccounts = memberListAccounts?.data?.map((item) => item.account) ?? [];

                // Filter RTBs for model accounts only
                const modelRtbs = branchRtbs?.data?.filter((item) => modelAccountIds.includes(item.accountId)) ?? [];

                // Combine accounts with their real-time balances
                const modelAccountsWithRtbs = combineAccountsWithRtbs(modelAccounts, modelRtbs);

                // Filter portfolio entries for model accounts
                const modelPortfolioUncombined =
                    branchPortfolio?.data?.entries?.filter((item) => modelAccountIds.includes(item.accountId)) ?? [];

                // Combine positions by symbol/type/securityType
                const modelPortfolioCombined = combinePositions(modelPortfolioUncombined);

                const modelSecurityTotalValue = getPortfolioSecurityTotalValue(modelPortfolioCombined);

                // Initialize portfolio with merged entries and default values
                // Note: These default values are placeholders - actual calculations
                // are performed by formatPortfolio function below
                const modelPortfolio: Portfolio = {
                    ...DEFAULT_PORTFOLIO_VALUES,
                    entries: modelPortfolioCombined,
                    currency: baseCurrency,
                    securityTotalValue: modelSecurityTotalValue,
                };

                // Format and enrich portfolio with calculated metrics
                const portfolio = formatPortfolio(modelPortfolio, modelAccountsWithRtbs, [], modelSecurityTotalValue, {
                    symbol,
                    positionType: positionType,
                });

                return {
                    data: portfolio,
                    ...queryStates,
                };
            }

            // Return null data if queries haven't succeeded yet
            return {
                data: null,
                ...queryStates,
            };
        },
    });
};

export interface GroupPortfolioByBranchParams extends Omit<BranchPortfolioQueryParams, 'combined'> {
    groupId: number;
}

/**
 * Custom hook to fetch and aggregate group portfolio data from branch portfolio
 *
 * This hook combines data from multiple sources:
 * - Branch real-time balances
 * - Group accounts
 * - Branch portfolio positions
 */
export const useGetGroupPortfolioFromBranchPortfolioQuery = (params: GroupPortfolioByBranchParams) => {
    const { groupId, symbol, positionType, enabled = true } = params || {};
    const baseCurrency = useCurrentBaseCurrency();

    return useQueries({
        queries: [
            { ...userQueries.userInformation(), enabled },
            { ...branchQueries.branchRtbs(), enabled },
            { ...groupQueries.groupsList(), enabled },
            { ...branchQueries.branchPortfolio({ currency: baseCurrency, combined: false, positionType }), enabled },
        ],
        combine: (results) => {
            const [{ data: userInfo }, { data: branchRtbs }, { data: groups }, { data: branchPortfolio }] = results;
            const queryStates = getQueryStates(results);

            const group = groups?.data?.find((item) => item.id === groupId);

            if (queryStates.isSuccess) {
                const groupAccountIds = group?.accountIds ?? [];
                const groupAccounts =
                    userInfo?.data.accountList?.filter((item) => groupAccountIds.includes(item.accountId)) ?? [];

                const groupRtbs = branchRtbs?.data?.filter((item) => groupAccountIds.includes(item.accountId)) ?? [];

                const groupAccountsWithRtbs = combineAccountsWithRtbs(groupAccounts, groupRtbs);

                const groupPortfolioUncombined =
                    branchPortfolio?.data?.entries?.filter((item) => groupAccountIds.includes(item.accountId)) ?? [];

                const groupPortfolioCombined = combinePositions(groupPortfolioUncombined);

                const groupSecurityTotalValue = getPortfolioSecurityTotalValue(groupPortfolioCombined);

                const groupPortfolio: Portfolio = {
                    entries: groupPortfolioCombined,
                    ...DEFAULT_PORTFOLIO_VALUES,
                    currency: baseCurrency,
                    securityTotalValue: groupSecurityTotalValue,
                };

                const portfolio = formatPortfolio(groupPortfolio, groupAccountsWithRtbs, [], groupSecurityTotalValue, {
                    symbol,
                    positionType: positionType,
                });

                return {
                    data: portfolio,
                    ...queryStates,
                };
            }

            return {
                data: null,
                ...queryStates,
            };
        },
    });
};
export type UseGetBranchInventoryQueryParams = {
    enabled?: boolean;
};

export const useGetBranchInventoryAccountsQuery = (params: UseGetBranchInventoryQueryParams = {}) =>
    useQuery({ ...branchQueries.branchInventory(), ...params });
